import { Fragment, FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Information } from "@/components/Information/Information";
import { useAppSelector } from "@/redux/hooks";
import { conversationSelector } from "@/pages/Private/redux/conversation/conversation.slice";
import { useResolveCcRecipientsQuery } from "@/pages/Private/redux/conversation/conversation.api";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { CcLeadChip } from "./CcLeadChip";

interface CcAttendee {
	identifier: string;
	display_name?: string | null;
}

/**
 * `ccReceiverEmail` is raw JSON on the conversation and has two historical
 * shapes: Unipile attendee objects (`{ identifier, display_name }`) on received
 * mail and plain email strings on manually scheduled replies — normalize both.
 */
const toCcAttendees = (raw: Conversation["ccReceiverEmail"]): CcAttendee[] => {
	if (!Array.isArray(raw)) {
		return [];
	}

	const attendees = raw
		.map((entry): CcAttendee | null => {
			if (typeof entry === "string") {
				return { identifier: entry };
			}

			if (
				entry &&
				typeof entry === "object" &&
				typeof (entry as CcAttendee).identifier === "string"
			) {
				return entry as CcAttendee;
			}

			return null;
		})
		.filter((attendee): attendee is CcAttendee => attendee !== null);

	// Dedupe by address so the identifier is a stable, unique render key.
	const seen = new Set<string>();

	return attendees.filter((attendee) => {
		const key = toLookupKey(attendee.identifier);

		if (seen.has(key)) {
			return false;
		}

		seen.add(key);

		return true;
	});
};

const toLookupKey = (identifier: string): string => identifier.trim().toLowerCase();

/**
 * "Cc:" line of a received email in the inbox thread (v4). Each CC'd address
 * that resolves to a known prospect renders as a rich {@link CcLeadChip};
 * unresolved addresses keep the plain `name (email)` fallback. The lookup is
 * one batch request per distinct CC list (deduped by RTKQ's query cache); the
 * component reads the resolved data from the conversation slice.
 */
export const CcRecipientsLine: FunctionComponent<{
	ccReceiverEmail: Conversation["ccReceiverEmail"];
}> = ({ ccReceiverEmail }) => {
	const { t } = useTranslation();

	const attendees = useMemo(() => toCcAttendees(ccReceiverEmail), [ccReceiverEmail]);

	const emails = useMemo(
		() =>
			[...new Set(attendees.map((attendee) => toLookupKey(attendee.identifier)))]
				.filter((email) => email.includes("@"))
				.sort(),
		[attendees]
	);

	useResolveCcRecipientsQuery(emails, { skip: emails.length === 0 });

	const { ccRecipients } = useAppSelector(conversationSelector);

	if (attendees.length === 0) {
		return null;
	}

	return (
		<div className="text-sm text-text-meta font-normal flex flex-wrap items-center gap-1">
			{t("inbox.cc.label")}
			{attendees.map((attendee, index) => {
				const resolved = ccRecipients[toLookupKey(attendee.identifier)];

				return (
					<Fragment key={attendee.identifier}>
						{resolved ? (
							<CcLeadChip recipient={resolved} />
						) : (
							<span>
								{attendee.display_name
									? `${attendee.display_name} (${attendee.identifier})`
									: attendee.identifier}
							</span>
						)}
						{index < attendees.length - 1 && ", "}
					</Fragment>
				);
			})}{" "}
			<Information information={t("inbox.cc.replyNotSentToCc")} />
		</div>
	);
};
